import { CommonModule, NgIf } from '@angular/common'
import { Component, DestroyRef, inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import { CUSTOM_DEDUCTIONS, CUSTOM_EARNINGS, REIMBURSEMENTS } from '../../../store/state.constants'
import { ModalsComponent } from '../modals/modals.component'
import { MemoizedSelector, select, Store } from '@ngrx/store'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { selectSalaryComponents } from '../../../store/salary-components/salary-components.selectors'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
  take,
  tap
} from 'rxjs'
import { SalaryComponent } from '../../../store/salary-components/salary-components.state'
import {
  selectCtcValue,
  selectRevisedCtcValue,
  selectRevisedSalaryData,
  selectRevisedStatutoryCompliances,
  selectSalaryData,
  selectStatutoryCompliances
} from '../../../store/employee-salary/employee-salary.selectors'
import {
  EmployeeSalary,
  ExtendedSalaryComponent,
  SalaryData,
  SalaryMode,
  StatutoryCompliances
} from '../../../store/employee-salary/employee-salary.state'
import {
  addSalaryData,
  salaryRevisionActions
} from '../../../store/employee-salary/employee-salary.actions'
import { ActivatedRoute } from '@angular/router'
import { IndianNumberInputFormatDirective } from '../../../directives/indian-number-input-format.directive'
import {
  selectEmployeeSalary,
  selectRevisedSalary
} from '../../../store/employee-salary/employee-salary.reducers'
import { User, UserRole } from '../../../store/auth/auth.state'
import { selectSelectedUser } from '../../../store/user/user.reducer'

@Component({
  selector: 'app-salary-data',
  imports: [NgIf, FormsModule, CommonModule, ReactiveFormsModule, IndianNumberInputFormatDirective],
  providers: [{ provide: LOCALE_ID, useValue: 'en-IN' }],
  templateUrl: './salary-data.component.html',
  styleUrl: './salary-data.component.scss'
})
export class SalaryDataComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private modalService = inject(NgbModal)
  private fb = inject(FormBuilder)

  currentMode!: SalaryMode
  salaryMode = SalaryMode
  form!: FormGroup

  //---Defaults---//
  ctcValue = 0
  basicPercentage = 50 //Value in percent
  hraPercentage = 0 //Value in percent, set in ngOnInit
  annualPfCap = 21600 // Hard coded as 1800/month in statutory compliances

  complianceSettings!: StatutoryCompliances
  earnings!: SalaryComponent[]
  earnings$!: Observable<SalaryComponent[]>
  earningsTabName = CUSTOM_EARNINGS
  deductions!: SalaryComponent[]
  deductions$!: Observable<SalaryComponent[]>
  deductionsTabName = CUSTOM_DEDUCTIONS
  reimbursements!: SalaryComponent[]
  reimbursements$!: Observable<SalaryComponent[]>
  reimbursementsTabName = REIMBURSEMENTS
  OtherComponentsModal = ModalsComponent

  //---State---//
  selectedComponentItem: SalaryComponent | null = null
  employerContribution = 0
  employeeContribution = 0
  isEditing = false // Whether any earning, deduction or reimbursement is being edited in the modal
  currentStep = 1
  addCompError: string | null = null
  searchTerm = ''
  private searchTerm$ = new BehaviorSubject<string>('')
  currentCtcValue = 0
  currency = 'INR'
  user!: User

  // --- Filtered Observables for Modals ---
  filteredEarnings$!: Observable<SalaryComponent[]>
  filteredDeductions$!: Observable<SalaryComponent[]>
  filteredReimbursements$!: Observable<SalaryComponent[]>

  async ngOnInit() {
    this.currentMode = this.route.snapshot.data['mode'] as SalaryMode
    const savedCtc = localStorage.getItem('currentCtc')
    if (savedCtc) {
      this.currentCtcValue = Number(savedCtc)
    }

    this.user = await firstValueFrom(
      this.store.pipe(
        select(selectSelectedUser),
        filter((user): user is User => !!user),
        take(1),
        tap(user => {
          this.isContractor(user) ? (this.hraPercentage = 0) : (this.hraPercentage = 40)
        })
      )
    )
    // Get the currency from the Salary
    let selector: MemoizedSelector<object, EmployeeSalary>
    this.currentMode === SalaryMode.CREATE
      ? (selector = selectEmployeeSalary)
      : (selector = selectRevisedSalary)
    const salary = await firstValueFrom(this.store.select(selector))
    this.currency = salary.ctc.currency

    this.initializeForm()
    this.initializeComponentState()
    // this.setSalaryData(salary.salaryData)
    this.store
      .select(selectSalaryData)
      .pipe(take(1))
      .subscribe(salary => {
        if (salary) {
          // this.form.patchValue(salary)
          this.setSalaryData(salary)
        }
      })
  }

  private initializeComponentState(): void {
    this.earnings$ = this.store.select(selectSalaryComponents(CUSTOM_EARNINGS))
    this.deductions$ = this.store.select(selectSalaryComponents(CUSTOM_DEDUCTIONS))
    this.reimbursements$ = this.store.select(selectSalaryComponents(REIMBURSEMENTS))
    // --- Create filtered observables for the modals ---
    this.filteredEarnings$ = this.createFilteredObservable(this.earnings$)
    this.filteredDeductions$ = this.createFilteredObservable(this.deductions$)
    this.filteredReimbursements$ = this.createFilteredObservable(this.reimbursements$)

    let selectors: {
      compliance: MemoizedSelector<object, StatutoryCompliances>
      ctc: MemoizedSelector<object, number>
      salary: MemoizedSelector<object, SalaryData>
    }

    switch (this.currentMode) {
      case SalaryMode.CREATE:
        selectors = {
          compliance: selectStatutoryCompliances,
          ctc: selectCtcValue,
          salary: selectSalaryData
        }
        break
      case SalaryMode.REVISE:
      case SalaryMode.UPDATE:
        selectors = {
          compliance: selectRevisedStatutoryCompliances,
          ctc: selectRevisedCtcValue,
          salary: selectRevisedSalaryData
        }
        break
      default:
        throw new Error(`Unknown salary mode: ${this.currentMode}`)
    }

    combineLatest([
      this.store.select(selectors.compliance),
      this.store.select(selectors.ctc),
      this.store.select(selectors.salary)
    ])
      .pipe(
        take(1),
        tap(([complianceSettings, ctcValue, salaryData]) => {
          if (!complianceSettings || ctcValue === undefined || ctcValue === null) {
            return
          }
          this.complianceSettings = complianceSettings
          this.ctcValue = ctcValue ?? 0
          //Checking basic amount instead of salaryData because Initial state has 0 as values instead of null,
          if (salaryData.basic && this.ctcValue === this.currentCtcValue) {
            this.setSalaryData(this.ensureIntegerAmounts(salaryData))
            this.recalculatePercentagesFromForm()
          } else {
            // this.recalculatePercentagesFromForm()
            this.calculateInitialValues()
          }

          this.setupFormValueChangeListeners()
          this.form.updateValueAndValidity()
          this.calculateSpecialAllowance()
        })
      )
      .subscribe()
  }

  private createFilteredObservable(
    source$: Observable<SalaryComponent[]>
  ): Observable<SalaryComponent[]> {
    return combineLatest([source$, this.searchTerm$]).pipe(
      map(([components, term]) => {
        if (!term?.trim()) {
          return components // If no search term, return the full list
        }
        const lowerCaseTerm = term.toLowerCase()
        return components.filter(component => component.name.toLowerCase().includes(lowerCaseTerm))
      })
    )
  }

  // Helper to ensure loaded data conforms to integer requirement
  private ensureIntegerAmounts(salaryData: SalaryData): SalaryData {
    const processedData = { ...salaryData }
    processedData.basic = Math.ceil(processedData.basic ?? 0)
    processedData.hra = Math.ceil(processedData.hra ?? 0)
    processedData.pf = Math.ceil(processedData.pf ?? 0)

    if (processedData.otherComponents) {
      processedData.otherComponents = processedData.otherComponents.map(comp => ({
        ...comp,
        employerContribution: Math.ceil(comp.employerContribution ?? 0),
        employeeContribution: Math.ceil(comp.employeeContribution ?? 0)
      }))
    }
    return processedData
  }

  private setupFormValueChangeListeners(): void {
    // --- Basic Changes ---
    this.form
      .get('basic')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newBasicValue => {
        if (!this.form.get('basic')?.valid || newBasicValue === null) return

        const ceiledNewBasic = Math.ceil(newBasicValue)
        const maxBasic = this.getMaxAllowedValue('basic')

        if (ceiledNewBasic > maxBasic) {
          this.form.get('basic')?.setValue(maxBasic, { emitEvent: false })
          this.updateBasicPercentage()
          this.calculateHraFromBasic()
        } else {
          // Set the ceilinged value back if it changed
          if (ceiledNewBasic !== newBasicValue) {
            this.form.get('basic')?.setValue(ceiledNewBasic, { emitEvent: false })
          }
          this.updateBasicPercentage()
          this.calculateHraFromBasic()
        }
      })

    // --- HRA Changes ---
    this.form
      .get('hra')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newHraValue => {
        if (!this.form.get('hra')?.valid || newHraValue === null) return

        const ceiledNewHra = Math.ceil(newHraValue)
        const maxHra = this.getMaxAllowedValue('hra')

        if (ceiledNewHra > maxHra) {
          this.form.get('hra')?.setValue(maxHra, { emitEvent: false })
          this.updateHraPercentage()
          this.calculateSpecialAllowance()
        } else {
          if (ceiledNewHra !== newHraValue) {
            this.form.get('hra')?.setValue(ceiledNewHra, { emitEvent: false })
          }
          this.updateHraPercentage()
          this.calculateSpecialAllowance()
        }
      })

    // --- PF Changes ---
    this.form
      .get('pf')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newPfValue => {
        if (!this.form.get('pf')?.valid || newPfValue === null) return

        const ceiledNewPf = Math.ceil(newPfValue)
        if (ceiledNewPf !== newPfValue) {
          this.form.get('pf')?.setValue(ceiledNewPf, { emitEvent: false })
        }
        this.calculateSpecialAllowance()
      })

    // --- Other Components Changes ---
    this.addedComponentItems.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.calculateSpecialAllowance()
      })
  }

  ngOnDestroy() {
    if (this.form.valid) {
      // Ensure payload has integers before dispatching
      const rawValue = this.form.getRawValue()
      const finalPayload = this.ensureIntegerAmounts(rawValue)

      switch (this.currentMode) {
        case SalaryMode.CREATE:
          this.store.dispatch(addSalaryData({ salaryData: finalPayload }))
          break
        case SalaryMode.REVISE:
        case SalaryMode.UPDATE:
          this.store.dispatch(salaryRevisionActions.updateSalaryData({ salaryData: finalPayload }))
          break
        default:
        // TODO: Add error to Sentry or logging service
        //const errorMessage = `ngOnDestroy: Unknown or unsupported salary mode encountered: ${this.currentMode}`
        //this.sentryService.captureException(new Error(errorMessage)) // Send to Sentry
      }
    }
  }

  initializeForm() {
    this.form = this.fb.group(
      {
        basic: [0, [Validators.required, Validators.min(0)]],
        hra: [0, [Validators.required, Validators.min(0)]],
        pf: [0, [Validators.required, Validators.min(0)]],
        otherComponents: this.fb.array([]),
        specialAllowance: [{ value: 0, disabled: true }, Validators.required]
      },
      { validators: this.ctcAllocationValidator() }
    )

    if (this.complianceSettings) {
      this.form.get('pf')?.addValidators(this.cappedAmountValidator.bind(this))
      this.form.get('pf')?.updateValueAndValidity()
    } else {
      // Defer or handle case where settings aren't ready
    }
  }

  // Form Level Validator (Checks sum against CTC)
  ctcAllocationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.ctcValue || !control) {
        return null
      }

      const formGroup = control as FormGroup
      const basic = formGroup.get('basic')?.value ?? 0
      const hra = formGroup.get('hra')?.value ?? 0
      const pf = formGroup.get('pf')?.value ?? 0
      const otherComponentsSum = this.getOtherComponentsSumInCtc() // Already integers

      const totalAllocated = Number(basic) + Number(hra) + Number(pf) + Number(otherComponentsSum)

      return totalAllocated > this.ctcValue ? { ctcExceeded: true } : null
    }
  }

  // PF Specific Capping Validator
  cappedAmountValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.complianceSettings) return null

    const maxAmount = this.complianceSettings.employerCapping
      ? Math.floor(this.annualPfCap)
      : Number.MAX_SAFE_INTEGER

    if (control.value !== null) {
      const controlValue = Math.ceil(control.value)
      if (controlValue > maxAmount) {
        control.setValue(maxAmount, { emitEvent: false })
      } else if (controlValue < 0) {
        return { negativeValue: true }
      } else if (control.value !== controlValue) {
        // If the original value had decimals, set the ceiled value back
        control.setValue(controlValue, { emitEvent: false })
      }
    }
    return null
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
      if (control.hasError('min')) {
        return 'Value cannot be negative'
      }
      if (control.hasError('negativeValue')) {
        return 'Value cannot be negative'
      }
      // Add pattern error if you add integer validation
      // if (control.hasError('pattern')) { return 'Please enter a whole number'; }
    }
    if (controlName === 'form' && this.form.hasError('ctcExceeded')) {
      // Use Math.floor on ctcValue for display consistency if desired
      const displayCTC = Math.floor(this.ctcValue)
      return `Total allocated salary components exceed the total CTC (${displayCTC}).`
    }
    return null
  }

  createOtherComponentGroup(componentData?: ExtendedSalaryComponent): FormGroup {
    return this.fb.group({
      id: [componentData?.id],
      belongsTo: [componentData?.belongsTo, Validators.required],
      name: [componentData?.name, Validators.required],
      description: [componentData?.description],
      componentType: [componentData?.componentType, Validators.required],
      info: this.fb.group({
        // ... info properties ...
        deductionPlan: [componentData?.info?.deductionPlan],
        payType: [componentData?.info?.payType],
        calculationType: [componentData?.info?.calculationType],
        paymentFrequency: [componentData?.info?.paymentFrequency],
        taxDeductionPreference: [componentData?.info?.taxDeductionPreference],
        taxable: [componentData?.info?.taxable ?? false],
        isInCTC: [componentData?.info?.isInCTC ?? false],
        isInFBP: [componentData?.info?.isInFBP ?? false],
        proRataBasis: [componentData?.info?.proRataBasis ?? false],
        EPFContribution: [componentData?.info?.EPFContribution ?? false],
        ESIContribution: [componentData?.info?.ESIContribution ?? false],
        optional: [componentData?.info?.optional ?? false]
      }),

      employerContribution: [
        Math.ceil(componentData?.employerContribution ?? 0),
        [Validators.required, Validators.min(0)]
      ],
      employeeContribution: [
        Math.ceil(componentData?.employeeContribution ?? 0),
        [Validators.required, Validators.min(0)]
      ]
    })
  }

  setSalaryData(salaryData: SalaryData): void {
    const otherComponentsArray = this.form.get('otherComponents') as FormArray<FormGroup>
    otherComponentsArray.clear({ emitEvent: false })

    if (salaryData.otherComponents && Array.isArray(salaryData.otherComponents)) {
      salaryData.otherComponents.forEach((component: ExtendedSalaryComponent) => {
        const componentGroup = this.createOtherComponentGroup(component)
        otherComponentsArray.push(componentGroup, { emitEvent: false })
      })
    }

    const { otherComponents: _, specialAllowance: _specialAllowance, ...topLevelData } = salaryData

    const { basic, hra, pf, ...restTopLevelData } = topLevelData
    // Ensure top-level amounts are integers before patching
    const integerTopLevelData = {
      // ...topLevelData,
      basic: Math.ceil(topLevelData.basic ?? 0),
      hra: Math.ceil(topLevelData.hra ?? 0),
      pf: Math.ceil(topLevelData.pf ?? 0),
      ...restTopLevelData
    }

    this.form.patchValue(integerTopLevelData, { emitEvent: false })
    // ✅ manually trigger calculations
    if (basic > 0) {
      this.updateBasicPercentage()
      this.updateHraPercentage()
      this.calculateHraFromBasic()
      // this.calculateSpecialAllowance()
    } else {
      this.calculateInitialValues()
    }

    this.form.updateValueAndValidity({ emitEvent: false })
    this.calculateSpecialAllowance() // Recalculate SA based on patched integer values
  }

  //12 feb 2026 gps
  // setSalaryData(salaryData: SalaryData): void {
  //   const otherComponentsArray = this.form.get('otherComponents') as FormArray<FormGroup>
  //   otherComponentsArray.clear({ emitEvent: false })

  //   if (salaryData.otherComponents && Array.isArray(salaryData.otherComponents)) {
  //     salaryData.otherComponents.forEach((component: ExtendedSalaryComponent) => {
  //       const componentGroup = this.createOtherComponentGroup(component)
  //       otherComponentsArray.push(componentGroup, { emitEvent: false })
  //     })
  //   }

  //   const { otherComponents: _, specialAllowance: _specialAllowance, ...topLevelData } = salaryData
  //   const { basic, hra, pf, ...restTopLevelData } = topLevelData

  //   const integerTopLevelData: any = {
  //     ...restTopLevelData
  //   }

  //   // Only include fields if they are NOT null or undefined
  //   if (basic !== null && basic !== 0) {
  //     integerTopLevelData.basic = Math.ceil(basic)
  //   }

  //   if (hra !== null && basic !== 0) {
  //     integerTopLevelData.hra = Math.ceil(hra)
  //   }

  //   if (pf !== null && basic !== 0) {
  //     integerTopLevelData.pf = Math.ceil(pf)
  //   }

  //   this.form.patchValue(integerTopLevelData, { emitEvent: false })
  //   this.form.updateValueAndValidity({ emitEvent: false })

  //   this.calculateSpecialAllowance()
  // }

  private recalculatePercentagesFromForm(): void {
    const basic = this.form.get('basic')?.value || 0
    const hra = this.form.get('hra')?.value || 0

    if (this.ctcValue > 0 && this.ctcValue === this.currentCtcValue) {
      this.basicPercentage = Math.round((basic / this.ctcValue) * 100 * 1000) / 1000
    } else if (this.ctcValue > 0 && this.ctcValue !== this.currentCtcValue) {
      this.basicPercentage = 50
      this.hraPercentage = 40
    } else {
      this.basicPercentage = 0
    }
    // if (basic > 0) {
    //   this.hraPercentage = Math.round((hra / basic) * 100 * 1000) / 1000
    // } else {
    //   this.hraPercentage = 0
    // }
  }

  //kripesh
  // calculateInitialValues() {
  //   if (this.ctcValue <= 0) {
  //     this.form.patchValue({ basic: 0, hra: 0, pf: 0 }, { emitEvent: false })
  //     this.calculateSpecialAllowance()
  //     return
  //   }

  //   // Calculate raw values
  //   const rawBasic = (this.ctcValue * this.basicPercentage) / 100
  //   const basic = Math.ceil(rawBasic)

  //   const rawHra = (basic * this.hraPercentage) / 100
  //   const hra = Math.ceil(rawHra)

  //   let pf = 0
  //   if (this.complianceSettings?.pf) {
  //     const rawPf = (0.12 * (this.ctcValue - hra)) / 1.12
  //     pf = Math.ceil(rawPf)

  //     // Apply capping if enabled - cap should be integer
  //     const intCap = Math.floor(this.annualPfCap)
  //     if (this.complianceSettings.employerCapping && pf > intCap) {
  //       pf = intCap
  //     }
  //   }

  //   this.form.patchValue({ basic: basic, hra: hra, pf: pf }, { emitEvent: false })
  //   this.calculateSpecialAllowance()
  // }

  //gps 7 january 2026
  calculateInitialValues() {
    if (this.ctcValue <= 0) {
      this.form.patchValue({ basic: 0, hra: 0, pf: 0, eps: 0, epf: 0 }, { emitEvent: false })
      this.calculateSpecialAllowance()
      return
    }

    // 1. Basic
    const rawBasic = (this.ctcValue * this.basicPercentage) / 100
    const basic = Math.ceil(rawBasic)

    // 2. HRA
    const rawHra = (basic * this.hraPercentage) / 100
    const hra = Math.ceil(rawHra)

    // 3. PF / EPS
    let pf = 0
    // let eps = 0
    // let epf = 0

    if (this.complianceSettings?.pf) {
      // Employer PF INCLUDED in CTC
      let employerPf = Math.ceil((0.12 * basic) / 1.12)

      // Employer PF capping (₹1,800 / month)
      if (this.complianceSettings.employerCapping) {
        // employerPf = Math.min(employerPf, 1800)
        employerPf = 1800 * 12
      }

      pf = employerPf

      // 4. EPS breakup (only if EPS is enabled)
      // if (this.complianceSettings.eps) {
      //   // EPS is 8.33% of ₹15,000 (max)
      //   eps = Math.min(Math.ceil(0.0833 * Math.min(basic, 15000)), 1249)

      //   // EPF is remainder
      //   epf = pf - eps
      // } else {
      //   // No EPS → full amount goes to EPF
      //   epf = pf
      // }
    }

    // 5. Patch values
    this.form.patchValue(
      {
        basic: basic,
        hra: hra,
        pf: pf
        // eps: eps,
        // epf: epf
      },
      { emitEvent: false }
    )

    // 6. Balance special allowance
    this.calculateSpecialAllowance()
  }

  // Calculates the maximum integer value a field can take without making special allowance negative
  getMaxAllowedValue(fieldName: 'basic' | 'hra' | 'pf'): number {
    const currentBasic = this.form.get('basic')?.value ?? 0
    const currentHra = this.form.get('hra')?.value ?? 0
    const currentPf = this.form.get('pf')?.value ?? 0
    const otherComponentsSum = this.getOtherComponentsSumInCtc() // Integers

    let fixedSum = 0
    switch (fieldName) {
      case 'basic':
        fixedSum = Number(currentHra) + Number(currentPf) + otherComponentsSum
        break
      case 'hra':
        fixedSum = Number(currentBasic) + Number(currentPf) + otherComponentsSum
        break
      case 'pf':
        fixedSum = Number(currentBasic) + Number(currentHra) + otherComponentsSum
        break
    }

    // Floor the result to get the max *integer* allowed without exceeding CTC
    const maxValue = Math.floor(this.ctcValue - fixedSum)
    return Math.max(0, maxValue)
  }

  updateBasicPercentage() {
    const basic = this.form.get('basic')?.value || 0
    if (this.ctcValue > 0) {
      this.basicPercentage = Math.round((basic / this.ctcValue) * 100 * 1000) / 1000
    } else {
      this.basicPercentage = 0
    }
  }

  updateHraPercentage() {
    const hra = this.form.get('hra')?.value || 0
    const basic = this.form.get('basic')?.value || 0
    if (basic > 0) {
      this.hraPercentage = Math.round((hra / basic) * 100 * 1000) / 1000
    } else {
      this.hraPercentage = 0
    }
  }

  updateBasicAmount(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const percentage = inputElement.valueAsNumber
    if (isNaN(percentage) || percentage < 0) return

    this.basicPercentage = percentage
    const rawBasicAmount = (this.ctcValue * this.basicPercentage) / 100
    const basicCeiled = Math.ceil(rawBasicAmount)

    const maxBasic = this.getMaxAllowedValue('basic')
    // console.log('percentage', percentage)
    // console.log('basic ceiled', basicCeiled)
    // console.log('maxBasic', maxBasic)
    if (basicCeiled > maxBasic) {
      this.form.get('basic')?.setValue(maxBasic)
      // Update percentage based on the capped integer value
      if (this.ctcValue > 0) {
        this.basicPercentage = Math.round((maxBasic / this.ctcValue) * 100 * 1000) / 1000
        inputElement.value = this.basicPercentage.toFixed(3)
      }
    } else {
      // Use setValue to trigger valueChanges listener with the integer value
      this.form.get('basic')?.setValue(basicCeiled)
    }
  }

  updateHraAmount(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const percentage = inputElement.valueAsNumber
    if (isNaN(percentage) || percentage < 0) return

    this.hraPercentage = percentage
    const basic = this.form.get('basic')?.value || 0
    const rawHraAmount = (basic * this.hraPercentage) / 100
    const hraCeiled = Math.ceil(rawHraAmount)

    const maxHra = this.getMaxAllowedValue('hra')

    if (hraCeiled > maxHra) {
      this.form.get('hra')?.setValue(maxHra)
      if (basic > 0) {
        this.hraPercentage = Math.round((maxHra / basic) * 100 * 1000) / 1000
        inputElement.value = this.hraPercentage.toFixed(3) // Update input display
      }
    } else {
      this.form.get('hra')?.setValue(hraCeiled)
    }
  }

  // --- Calculation Helpers ---
  calculateHraFromBasic() {
    const basic = this.form.get('basic')?.value || 0
    const rawHraAmount = (basic * this.hraPercentage) / 100
    const hraCeiled = Math.ceil(rawHraAmount)

    const maxHra = this.getMaxAllowedValue('hra')

    if (hraCeiled > maxHra) {
      this.form.get('hra')?.setValue(maxHra, { emitEvent: false })
      this.updateHraPercentage()
      this.calculateSpecialAllowance()
    } else {
      this.form.get('hra')?.setValue(hraCeiled, { emitEvent: false })
      this.calculateSpecialAllowance()
    }
  }

  // kripesh
  // private getOtherComponentsSumInCtc(): number {
  //   return (
  //     this.addedComponentItems.controls
  //       .filter(ctrl => ctrl.get('info.isInCTC')?.value)
  //       .reduce((sum: number, item: AbstractControl) => {
  //         const contribution = item.get('employerContribution')?.value ?? 0
  //         return sum + Number(contribution)
  //       }, 0) || 0
  //   )
  // }

  //gps 27 january 2026
  private getOtherComponentsSumInCtc(): number {
    return this.addedComponentItems.controls
      .filter(ctrl => ctrl.get('info')?.get('isInCTC')?.value)
      .reduce((sum: number, item: AbstractControl) => {
        const employeeContribution = Number(item.get('employeeContribution')?.value || 0)
        const employerContribution = Number(item.get('employerContribution')?.value || 0)
        return sum + employeeContribution + employerContribution
      }, 0)
  }

  calculateSpecialAllowance() {
    const basic = this.form.get('basic')?.value || 0
    const hra = this.form.get('hra')?.value || 0
    const pf = this.form.get('pf')?.value || 0
    const otherComponentsSumInCtc = this.getOtherComponentsSumInCtc()

    const totalAllocatedInCtc = Number(basic) + Number(hra) + Number(pf) + otherComponentsSumInCtc

    const specialAllowanceAmount = Math.floor(this.ctcValue - totalAllocatedInCtc)
    // Ensure special allowance isn't negative
    const finalSpecialAllowance = Math.max(0, specialAllowanceAmount)

    if (this.form.get('specialAllowance')?.value !== finalSpecialAllowance) {
      this.form.patchValue({ specialAllowance: finalSpecialAllowance }, { emitEvent: false })
    }

    this.form.updateValueAndValidity({ emitEvent: false })
  }
  // --- /Calculation Helpers ---

  openModel(modal: unknown, componentName = '') {
    this.addCompError = null // Clear previous error
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'md'
    })
    if (modalRef.componentInstance) modalRef.componentInstance.salaryComponentName = componentName
  }

  onSearchChange(): void {
    this.searchTerm$.next(this.searchTerm)
  }

  onClose() {
    this.selectedComponentItem = null
    this.employerContribution = 0
    this.employeeContribution = 0
    this.addCompError = null
    this.isEditing = false
    this.searchTerm = ''
    this.searchTerm$.next('')
    this.modalService.dismissAll()
  }

  // --- Getters for FormArray and added items ---
  get addedComponentItems(): FormArray {
    return this.form.get('otherComponents') as FormArray
  }
  get notInCtcComponents() {
    return this.addedComponentItems.controls.filter(item => !item.get('info.isInCTC')?.value)
  }
  get inCtcComponents() {
    return this.addedComponentItems.controls.filter(item => item.get('info.isInCTC')?.value)
  }

  isComponentItemAdded(item: SalaryComponent): boolean {
    return this.addedComponentItems.value.some(
      (added: { id?: string; name?: string }) => added.id === item.id || added.name === item.name // Check by ID or name
    )
  }

  onRadioChange(item: SalaryComponent) {
    this.addCompError = null
    this.selectedComponentItem = item
    this.isEditing = false // Selecting a new item is always "add" mode
    this.employerContribution = 0 // Reset contributions for new selection
    this.employeeContribution = 0
    //if (!this.isComponentItemAdded(item)) {
    //  this.selectedComponentItem = item
    //} else {
    //  this.selectedComponentItem = null
    //}
  }

  // getAddedContributionOfEmployer(item: SalaryComponent): number {
  //   // Reads integer value from form
  //   const found = this.addedComponentItems.controls.find(
  //     ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
  //   )
  //   return found ? (found.get('employerContribution')?.value ?? 0) : 0
  // }
  // getAddedContributionOfEmployee(item: SalaryComponent): number {
  //   // Reads integer value from form
  //   const found = this.addedComponentItems.controls.find(
  //     ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
  //   )
  //   return found ? (found.get('employeeContribution')?.value ?? 0) : 0
  // }

  startEditing(item: SalaryComponent): void {
    this.isEditing = true
    this.selectedComponentItem = item
    this.employerContribution = this.getAddedContributionOfEmployer(item)
    this.employeeContribution = this.getAddedContributionOfEmployee(item)
    this.addCompError = null
  }

  // --- Add/Remove Component Items ---
  addComponentItem(): void {
    this.addCompError = null

    if (!this.selectedComponentItem) {
      this.addCompError = 'Please select a component to add.'
      return
    }

    const employerContributionCeiled = Math.ceil(this.employerContribution ?? 0)
    const employeeContributionCeiled = Math.ceil(this.employeeContribution ?? 0)

    if (employerContributionCeiled < 0 || employeeContributionCeiled < 0) {
      this.addCompError = 'Contribution amounts cannot be negative.'
      return
    }

    // --- CTC Check with Integers ---
    const basic = this.form.get('basic')?.value || 0
    const hra = this.form.get('hra')?.value || 0
    const pf = this.form.get('pf')?.value || 0
    let currentOtherComponentsSumInCtc = this.getOtherComponentsSumInCtc()

    // If editing, temporarily subtract the item's old contribution from the total for an accurate check
    if (this.isEditing) {
      const oldItemContribution = this.getAddedContributionOfEmployer(this.selectedComponentItem)
      currentOtherComponentsSumInCtc -= oldItemContribution
    }
    const newComponentEmployerContribution = this.selectedComponentItem.info?.isInCTC
      ? employerContributionCeiled
      : 0

    const potentialTotalAllocated =
      Number(basic) +
      Number(hra) +
      Number(pf) +
      currentOtherComponentsSumInCtc +
      newComponentEmployerContribution

    if (potentialTotalAllocated > this.ctcValue) {
      const overAmount = potentialTotalAllocated - this.ctcValue
      this.addCompError = `Cannot add component. Adding ${this.selectedComponentItem.name} (${newComponentEmployerContribution}) would exceed CTC by ${overAmount.toFixed(0)}.` // Show integer over amount
      return
    }
    // --- End CTC Check ---

    // If we were editing, remove the old item from the form array.
    if (this.isEditing) {
      const index = this.addedComponentItems.controls.findIndex(
        ctrl => ctrl.value.id === this.selectedComponentItem!.id
      )
      if (index > -1) {
        this.addedComponentItems.removeAt(index)
      }
    }

    // Create the new item using the ceilinged contributions
    const newItemData: ExtendedSalaryComponent = {
      ...this.selectedComponentItem,
      employerContribution: employerContributionCeiled,
      employeeContribution: employeeContributionCeiled
    }
    const newItemFormGroup = this.createOtherComponentGroup(newItemData) // create group handles ceiling again, redundant but safe

    this.addedComponentItems.push(newItemFormGroup)

    // Reset modal inputs
    this.onClose()
  }

  // Remove component item by index
  removeComponentItem(index: number): void {
    if (index >= 0 && index < this.addedComponentItems.length) {
      this.addedComponentItems.removeAt(index)
    }
  }

  // Remove component item and update modal UI
  removeComponentItemFromModal(item: SalaryComponent): void {
    const index = this.addedComponentItems.controls.findIndex(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )

    if (index > -1) {
      this.removeComponentItem(index) // Reuse existing logic
    }

    // If the deleted item was the one selected for editing, reset the modal state.
    if (this.selectedComponentItem?.id === item.id) {
      this.selectedComponentItem = null
      this.isEditing = false
      this.employerContribution = 0
      this.employeeContribution = 0
    }
  }

  isContractor(employee: any): boolean {
    return employee?.userRoles?.some((r: UserRole) => r?.role?.name === 'Contractor') || false
  }

  private calculateEmployerEsi(monthlyGross: number, manualValue?: number): number {
    if (manualValue !== undefined) return manualValue

    const esiRate = (this.complianceSettings as any)?.esiEmployerRate ?? 3.25
    const esiLimit = (this.complianceSettings as any)?.esiLimit ?? 21000

    if (monthlyGross <= esiLimit) {
      return Math.floor(monthlyGross * (esiRate / 100))
    }
    return 0
  }

  private calculateEmployeeEsi(monthlyGross: number, manualValue?: number): number {
    if (manualValue !== undefined) return manualValue

    const esiRate = (this.complianceSettings as any)?.esiEmployeeRate ?? 0.75
    const esiLimit = (this.complianceSettings as any)?.esiLimit ?? 21000

    if (monthlyGross <= esiLimit) {
      return Math.floor(monthlyGross * (esiRate / 100))
    }
    return 0
  }

  getAddedContributionOfEmployer(item: SalaryComponent): number {
    if (item.info?.ESIContribution && this.complianceSettings?.esi) {
      const grossMonthly =
        (this.form.get('basic')?.value ?? 0) / 12 +
        (this.form.get('hra')?.value ?? 0) / 12 +
        (this.form.get('specialAllowance')?.value ?? 0) / 12

      const esiComponent = this.addedComponentItems.controls.find(
        ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
      )

      const manualEmployer = esiComponent?.get('employerContribution')?.value ?? undefined
      return this.calculateEmployerEsi(grossMonthly, manualEmployer)
    }

    const found = this.addedComponentItems.controls.find(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )

    return found ? (found.get('employerContribution')?.value ?? 0) : 0
  }

  getAddedContributionOfEmployee(item: SalaryComponent): number {
    if (item.info?.ESIContribution && this.complianceSettings?.esi) {
      const grossMonthly =
        (this.form.get('basic')?.value ?? 0) / 12 +
        (this.form.get('hra')?.value ?? 0) / 12 +
        (this.form.get('specialAllowance')?.value ?? 0) / 12

      const esiComponent = this.addedComponentItems.controls.find(
        ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
      )

      const manualEmployee = esiComponent?.get('employeeContribution')?.value ?? undefined
      return this.calculateEmployeeEsi(grossMonthly, manualEmployee)
    }

    const found = this.addedComponentItems.controls.find(
      ctrl => ctrl.value.id === item.id || ctrl.value.name === item.name
    )

    return found ? (found.get('employeeContribution')?.value ?? 0) : 0
  }

  // calculateSpecialAllowance() {
  //   const basicAnnual = this.form.get('basic')?.value || 0
  //   const hraAnnual = this.form.get('hra')?.value || 0
  //   const pfAnnual = this.form.get('pf')?.value || 0

  //   const fixedOtherAnnual = this.getOtherComponentsSumInCtc()

  //   // Monthly values
  //   const basicM = basicAnnual / 12
  //   const hraM = hraAnnual / 12
  //   const pfM = pfAnnual / 12
  //   const fixedOtherM = fixedOtherAnnual / 12

  //   let specialM = this.getMonthlyCtc() - (basicM + hraM + pfM + fixedOtherM)
  //   specialM = Math.max(0, Math.floor(specialM))

  //   const grossM = basicM + hraM + specialM

  //   // --- Dynamic ESI Calculation ---
  //   let employerEsi = 0
  //   let employeeEsi = 0

  //   const esiComponent = this.addedComponentItems.controls.find(
  //     ctrl => ctrl.value.info?.ESIContribution === true
  //   )

  //   if (esiComponent) {
  //     // Use user-entered values as base
  //     const enteredEmployer = esiComponent.get('employerContribution')?.value ?? 0
  //     const enteredEmployee = esiComponent.get('employeeContribution')?.value ?? 0

  //     // Recalculate dynamically using your existing helper
  //     const esi = this.calculateEsiFromGross(grossM, enteredEmployer, enteredEmployee)

  //     employerEsi = esi.employer
  //     employeeEsi = esi.employee
  //   } else if (this.complianceSettings?.esi) {
  //     // No ESI component → calculate automatically
  //     const esi = this.calculateEsiFromGross(grossM)
  //     employerEsi = esi.employer
  //     employeeEsi = esi.employee
  //   }

  //   const finalSpecialM =
  //     this.getMonthlyCtc() - (basicM + hraM + pfM + fixedOtherM + employerEsi + employeeEsi)

  //   const finalSpecialAnnual = Math.max(0, Math.floor(finalSpecialM)) * 12

  //   this.form.patchValue({ specialAllowance: finalSpecialAnnual }, { emitEvent: false })

  //   // Optional: store monthly ESI for display or further calculation
  //   this.form.patchValue(
  //     {
  //       employerEsiMonthly: employerEsi,
  //       employeeEsiMonthly: employeeEsi
  //     },
  //     { emitEvent: false }
  //   )
  // }

  private getMonthlyCtc(): number {
    return this.ctcValue / 12
  }

  // private calculateEsiFromGross(grossMonthly: number) {
  //   if (grossMonthly > 21000) {
  //     return { employer: 0, employee: 0 }
  //   }

  //   return {
  //     employer: Math.floor(grossMonthly * 0.0325),
  //     employee: Math.floor(grossMonthly * 0.0075)
  //   }
  // }

  private calculateEsiFromGross(
    grossMonthly: number,
    enteredEmployer?: number,
    enteredEmployee?: number
  ) {
    // If user has entered values, use them
    if (enteredEmployer !== undefined && enteredEmployee !== undefined) {
      return {
        employer: enteredEmployer,
        employee: enteredEmployee
      }
    }

    // Automatic calculation based on compliance rules
    if (grossMonthly > 21000) {
      return { employer: 0, employee: 0 }
    }

    return {
      employer: Math.floor(grossMonthly * 0.0325),
      employee: Math.floor(grossMonthly * 0.0075)
    }
  }
}
